import React, { useState, useEffect } from 'react';
import {
  Grid, Segment, Form, Button, Message, Divider, Label, Icon,
} from 'semantic-ui-react';
import apiRequest from '../../app/common/api/apiClient';

const ROLE_COLORS = { superadmin: 'red', ksi: 'teal', knra: 'blue', user: 'grey' };

const AccountPage = () => {
  const [profile, setProfile] = useState({ nom: '', prenom: '', telephone: '', fonctionpro: '', etablissement: '' });
  const [profileMsg, setProfileMsg] = useState(null);

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordMsg, setPasswordMsg] = useState(null);

  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    apiRequest('/api/users/me')
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setProfile({
          nom: data.nom || '',
          prenom: data.prenom || '',
          telephone: data.telephone || '',
          fonctionpro: data.fonctionpro || '',
          etablissement: data.etablissement || '',
          email: data.email || '',
          type_utilisateur: data.type_utilisateur || '',
          date_utilisateur: data.date_utilisateur || null,
        });
      })
      .catch((err) => console.error(err));
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((p) => ({ ...p, [name]: value }));
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    apiRequest('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify({
        nom: profile.nom,
        prenom: profile.prenom,
        telephone: profile.telephone,
        fonctionpro: profile.fonctionpro,
        etablissement: profile.etablissement,
      }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        return data;
      })
      .then(() => setProfileMsg({ type: 'success', text: 'Profile updated successfully.' }))
      .catch((err) => setProfileMsg({ type: 'error', text: err.message || 'Error updating profile.' }))
      .finally(() => setSaving(false));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((p) => ({ ...p, [name]: value }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (passwords.newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    setSavingPwd(true);
    apiRequest('/api/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        setPasswordMsg({ type: 'success', text: 'Password changed successfully.' });
        setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      })
      .catch((err) => setPasswordMsg({ type: 'error', text: err.message || 'Error changing password.' }))
      .finally(() => setSavingPwd(false));
  };

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={8}>
          <Segment.Group>
            <Segment attached='top'>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ margin: 0 }}>My account</h5>
                <Label color={ROLE_COLORS[profile.type_utilisateur] || 'grey'}>
                  <Icon name='user' />{profile.type_utilisateur}
                </Label>
              </div>
            </Segment>
            <Segment attached='bottom'>
              <p style={{ color: '#888', marginBottom: '1rem' }}>
                <Icon name='mail' />{profile.email}
                {profile.date_utilisateur && (
                  <> &nbsp;·&nbsp; Member since {new Date(profile.date_utilisateur).toLocaleDateString('en-GB')}</>
                )}
              </p>

              {profileMsg && (
                <Message positive={profileMsg.type === 'success'} negative={profileMsg.type === 'error'}>
                  {profileMsg.text}
                </Message>
              )}

              <Form onSubmit={handleProfileSubmit}>
                <Form.Group widths='equal'>
                  <Form.Input label='Last name' name='nom' value={profile.nom} onChange={handleProfileChange} placeholder='Last name' />
                  <Form.Input label='First name' name='prenom' value={profile.prenom} onChange={handleProfileChange} placeholder='First name' />
                </Form.Group>
                <Form.Input label='Phone' name='telephone' value={profile.telephone} onChange={handleProfileChange} placeholder='+670 ...' />
                <Form.Group widths='equal'>
                  <Form.Input label='Job title' name='fonctionpro' value={profile.fonctionpro} onChange={handleProfileChange} placeholder='e.g. Lawyer...' />
                  <Form.Input label='Organisation' name='etablissement' value={profile.etablissement} onChange={handleProfileChange} placeholder='e.g. KSI, NGO...' />
                </Form.Group>
                <Button primary content='Save changes' type='submit' loading={saving} disabled={saving} />
              </Form>
            </Segment>
          </Segment.Group>
        </Grid.Column>

        <Grid.Column width={8}>
          <Segment.Group>
            <Segment attached='top'><h5>Change password</h5></Segment>
            <Segment attached='bottom'>
              {passwordMsg && (
                <Message positive={passwordMsg.type === 'success'} negative={passwordMsg.type === 'error'}>
                  {passwordMsg.text}
                </Message>
              )}
              <Form onSubmit={handlePasswordSubmit}>
                <Form.Input label='Current password' name='currentPassword' type='password' value={passwords.currentPassword} onChange={handlePasswordChange} placeholder='Current password' />
                <Divider />
                <Form.Input label='New password' name='newPassword' type='password' value={passwords.newPassword} onChange={handlePasswordChange} placeholder='Minimum 8 characters' />
                <Form.Input label='Confirm new password' name='confirmPassword' type='password' value={passwords.confirmPassword} onChange={handlePasswordChange} placeholder='Repeat new password' />
                <Button primary content='Change password' type='submit' loading={savingPwd} disabled={savingPwd} />
              </Form>
            </Segment>
          </Segment.Group>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default AccountPage;
